import { toast } from "@credit-acceptance/uikit";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { usePullWorknumber } from "../api/usePullWorknumber";
import { useWorknumberEligibility } from "../api/useWorknumberEligibility";
import { useWorknumberHistory } from "../api/useWorknumberHistory";
import { PurchasedEmployerInfoPanel } from "./PurchasedEmployerInfoPanel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PullPurchasedEmployerInfoProperties {
  /** Deal / approval identifier — path param for all three worknumber calls. */
  approvalId?: string;
  /** Applicant identifier — path param for all three worknumber calls. */
  applicantId?: string;
  /** Stable id for the results table (aria + testing). */
  panelId: string;
}

// ---------------------------------------------------------------------------
// PullPurchasedEmployerInfo
//
// Orchestrates the "Pull Purchased Employer Info" flow for one applicant income
// block:
//   1. /eligibility runs first. `canPull === true` → offer the pull button.
//   2. Button click → POST /worknumber pulls fresh history → render the table.
//   3. `canPull === false` → read /history; render the table if prior data exists.
//
// The whole feature stays inert until both ids are present — approvalId and
// applicantId are both threaded from the dashboard response, so it never
// fires a doomed request.
//
// Every endpoint failure (eligibility, history, pull) is surfaced as a toast
// rather than inline text.
// ---------------------------------------------------------------------------
export function PullPurchasedEmployerInfo({ approvalId, applicantId, panelId }: PullPurchasedEmployerInfoProperties) {
  const { t } = useTranslation();
  const hasIds = Boolean(approvalId && applicantId);

  const eligibility = useWorknumberEligibility(approvalId ?? "", applicantId ?? "", { enabled: hasIds });
  const canPull = eligibility.data?.canPull === true;
  const notEligible = eligibility.isSuccess && !canPull;

  // History is the fallback read — only when the applicant can't pull fresh data.
  const history = useWorknumberHistory(approvalId ?? "", applicantId ?? "", { enabled: hasIds && notEligible });

  const pull = usePullWorknumber();

  // TanStack Query v5 dropped per-query `onError`, so query failures are
  // toasted from an effect. Each ref tracks the last-toasted error so a
  // re-render (rather than a new failure) doesn't re-fire the same toast.
  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedEligibilityErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (eligibility.isError && eligibility.error !== toastedEligibilityErrorReference.current) {
      toastedEligibilityErrorReference.current = eligibility.error;
      toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(eligibility.error) }));
    } else if (!eligibility.isError) {
      toastedEligibilityErrorReference.current = undefined;
    }
  }, [eligibility.isError, eligibility.error, t]);

  // eslint-disable-next-line unicorn/no-useless-undefined -- useRef's no-init overload still requires the arg here
  const toastedHistoryErrorReference = useRef<unknown>(undefined);
  useEffect(() => {
    if (history.isError && history.error !== toastedHistoryErrorReference.current) {
      toastedHistoryErrorReference.current = history.error;
      toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(history.error) }));
    } else if (!history.isError) {
      toastedHistoryErrorReference.current = undefined;
    }
  }, [history.isError, history.error, t]);

  if (!hasIds) {
    return null;
  }

  if (eligibility.isLoading) {
    return <p className="text-muted-foreground text-xs">{t("review.income.employerHistory.loading")}</p>;
  }

  // Eligibility failures still hide the feature (no button to offer) — the
  // toast above tells the analyst why it disappeared.
  if (eligibility.isError) {
    return null;
  }

  // Eligible: offer the pull button, then render whatever it pulls.
  if (canPull) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="link"
            size="xs"
            className="cursor-pointer px-0 underline"
            disabled={pull.isPending}
            onClick={() =>
              pull.mutate(
                { approvalId: approvalId ?? "", applicantId: applicantId ?? "" },
                {
                  onError: (error) => {
                    toast.error(t("review.income.employerHistory.error", { message: getErrorMessage(error) }));
                  },
                },
              )
            }
          >
            {pull.isPending ? t("review.income.employerHistory.pulling") : t("review.income.pullEmployerInfo")}
          </Button>
        </div>
        {pull.data && pull.data.rows.length > 0 && (
          <PurchasedEmployerInfoPanel panelId={panelId} rows={pull.data.rows} />
        )}
      </div>
    );
  }

  // Not eligible: surface previously-pulled history if any exists.
  if (history.isLoading) {
    return <p className="text-muted-foreground text-xs">{t("review.income.employerHistory.loading")}</p>;
  }

  if (history.data && history.data.rows.length > 0) {
    return <PurchasedEmployerInfoPanel panelId={panelId} rows={history.data.rows} />;
  }

  return null;
}
